import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import type { AnalyticsRange } from "../../stats/api/statsApi";
import {
  useDesktopBreakdownQuery,
  useDesktopOverviewQuery,
} from "../../stats/hooks/useStats";
import {
  BreakdownTable,
  StatsCard,
} from "../../stats/components/subcomponents";

interface AppViewProps {
  range: AnalyticsRange;
}

export const AppView = ({ range }: AppViewProps) => {
  const { t } = useTranslation("common");
  const overview = useDesktopOverviewQuery(range);
  const cpuModel = useDesktopBreakdownQuery(range, "cpu_model");
  const ramGb = useDesktopBreakdownQuery(range, "ram_gb");
  const gpuModel = useDesktopBreakdownQuery(range, "gpu_model");
  const gpuVendor = useDesktopBreakdownQuery(range, "gpu_vendor");
  const osPlatform = useDesktopBreakdownQuery(range, "os_platform");
  const osRelease = useDesktopBreakdownQuery(range, "os_release");
  const arch = useDesktopBreakdownQuery(range, "arch");
  const appVersion = useDesktopBreakdownQuery(range, "app_version");

  const isFetching =
    overview.isFetching ||
    cpuModel.isFetching ||
    ramGb.isFetching ||
    gpuModel.isFetching ||
    gpuVendor.isFetching ||
    osPlatform.isFetching ||
    osRelease.isFetching ||
    arch.isFetching ||
    appVersion.isFetching;

  return (
    <div className="relative space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatsCard
          title={t("APP.installs")}
          value={overview.data?.installs ?? 0}
        />
        <StatsCard
          title={t("APP.sessions")}
          value={overview.data?.sessions ?? 0}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">
              {t("APP.topCpu")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownTable
              rows={cpuModel.data ?? []}
              isLoading={cpuModel.isLoading}
              labelHeader={t("APP.cpu")}
              valueHeader={t("APP.installs")}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">
              {t("APP.topRam")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownTable
              rows={ramGb.data ?? []}
              isLoading={ramGb.isLoading}
              labelHeader={t("APP.ram")}
              valueHeader={t("APP.installs")}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">
              {t("APP.topGpu")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownTable
              rows={gpuModel.data ?? []}
              isLoading={gpuModel.isLoading}
              labelHeader={t("APP.gpu")}
              valueHeader={t("APP.installs")}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">
              {t("APP.topGpuVendor")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownTable
              rows={gpuVendor.data ?? []}
              isLoading={gpuVendor.isLoading}
              labelHeader={t("APP.gpuVendor")}
              valueHeader={t("APP.installs")}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">
              {t("APP.topOs")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownTable
              rows={osPlatform.data ?? []}
              isLoading={osPlatform.isLoading}
              labelHeader={t("APP.osPlatform")}
              valueHeader={t("APP.installs")}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">
              {t("APP.topOsRelease")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownTable
              rows={osRelease.data ?? []}
              isLoading={osRelease.isLoading}
              labelHeader={t("APP.osRelease")}
              valueHeader={t("APP.installs")}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">
              {t("APP.topArch")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownTable
              rows={arch.data ?? []}
              isLoading={arch.isLoading}
              labelHeader={t("APP.arch")}
              valueHeader={t("APP.installs")}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">
              {t("APP.topVersion")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BreakdownTable
              rows={appVersion.data ?? []}
              isLoading={appVersion.isLoading}
              labelHeader={t("APP.version")}
              valueHeader={t("APP.installs")}
            />
          </CardContent>
        </Card>
      </div>

      {isFetching && !overview.isLoading ? <LoadingOverlay /> : null}
    </div>
  );
};
